import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Aviso, Botao, Chapa, Selo, type Estagio } from '../componentes/forma.tsx';
import { api, ErroApi } from '../lib/api.ts';
import { dataBr } from '../lib/formato.ts';
import './quarentena.css';

interface Preso {
  id: string;
  codigoLido: string | null;
  pacienteNome: string | null;
  pacienteNascimento: string | null;
  dataExame: string | null;
  serie: string | null;
  descricaoEstudo: string | null;
  motivo: string;
  recebidoEm: string;
}

interface Sugestao {
  id: string;
  codigo: number;
  dataExame: string;
  situacao: Estagio;
  pacienteNome: string;
  procedimentoNome: string;
}

export function Quarentena() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [aberto, setAberto] = useState<string | null>(null);
  const [erro, setErro] = useState('');

  const lista = useQuery({
    queryKey: ['quarentena'],
    queryFn: ({ signal }) =>
      api.get<{ itens: Preso[]; total: number; motivos: Record<string, string> }>(
        '/quarentena',
        signal,
      ),
    refetchInterval: 60_000,
  });

  const sugestoes = useQuery({
    queryKey: ['quarentena', aberto, 'sugestoes'],
    queryFn: ({ signal }) => api.get<{ itens: Sugestao[] }>(`/quarentena/${aberto}/sugestoes`, signal),
    enabled: !!aberto,
  });

  const invalidar = () => {
    void qc.invalidateQueries({ queryKey: ['quarentena'] });
    void qc.invalidateQueries({ queryKey: ['exames'] });
  };

  const vincular = useMutation({
    mutationFn: (v: { id: string; exameId: string }) =>
      api.post<{ codigo: number }>(`/quarentena/${v.id}/vincular`, { exameId: v.exameId }),
    onSuccess: () => {
      invalidar();
      setAberto(null);
      setErro('');
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível vincular.'),
  });

  const descartar = useMutation({
    mutationFn: (id: string) => api.apagar(`/quarentena/${id}`),
    onSuccess: () => {
      invalidar();
      setAberto(null);
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.message : 'Não foi possível descartar.'),
  });

  const itens = lista.data?.itens ?? [];
  const motivos = lista.data?.motivos ?? {};

  return (
    <Layout>
      <Cabecalho
        titulo="Imagens sem exame"
        contagem={lista.isPending ? '' : `${itens.length}`}
      />

      {erro && (
        <div style={{ marginBottom: 'var(--e-3)' }}>
          <Aviso>{erro}</Aviso>
        </div>
      )}

      {lista.isPending ? (
        <p className="vazio">Carregando…</p>
      ) : itens.length === 0 ? (
        <div className="cartao vazio">
          <p className="vazio__titulo">Nenhuma imagem parada</p>
          <p>
            Toda imagem que chegou da sala de RX encontrou o exame dela. É assim que deve ficar
            na maior parte do tempo.
          </p>
        </div>
      ) : (
        <>
          <div className="quarentena__explica cartao">
            <p>
              Estas imagens chegaram do eFilm mas não encontraram exame. Quase sempre é um dígito
              errado no código digitado pelo técnico. O arquivo original está guardado — vincule
              ao exame certo e a imagem entra nele como se tivesse chegado direto.
            </p>
          </div>

          <ul className="quarentena__lista">
            {itens.map((p) => (
              <li key={p.id} className="cartao preso" data-aberto={aberto === p.id || undefined}>
                <div className="preso__topo">
                  <a
                    href={`/api/quarentena/${p.id}/imagem/web`}
                    target="_blank"
                    rel="noreferrer"
                    title="Ampliar"
                  >
                    <img
                      className="preso__miniatura"
                      src={`/api/quarentena/${p.id}/imagem`}
                      alt={`Imagem de ${p.pacienteNome ?? 'paciente não identificado'}`}
                    />
                  </a>

                  <div className="preso__dados">
                    <p className="preso__motivo">{motivos[p.motivo] ?? p.motivo}</p>
                    <h2 className="preso__nome">{p.pacienteNome ?? 'Sem nome no arquivo'}</h2>
                    <dl className="preso__campos">
                      <div>
                        <dt className="rotulo">Código digitado</dt>
                        <dd className="mono">{p.codigoLido ?? '— em branco —'}</dd>
                      </div>
                      <div>
                        <dt className="rotulo">Data do exame</dt>
                        <dd className="mono">{dataBr(p.dataExame)}</dd>
                      </div>
                      <div>
                        <dt className="rotulo">Nascimento</dt>
                        <dd className="mono">{dataBr(p.pacienteNascimento) || '—'}</dd>
                      </div>
                      <div>
                        <dt className="rotulo">Incidência</dt>
                        <dd>{p.serie ?? '—'}</dd>
                      </div>
                    </dl>
                    {p.descricaoEstudo && (
                      <p className="preso__estudo">
                        Protocolo no eFilm: {p.descricaoEstudo}
                        <span className="preso__ressalva">
                          — o técnico raramente troca o protocolo, então isto pode não
                          corresponder ao exame real.
                        </span>
                      </p>
                    )}
                  </div>

                  <div className="preso__acoes">
                    <Botao
                      variante={aberto === p.id ? 'neutro' : 'primario'}
                      onClick={() => {
                        setAberto(aberto === p.id ? null : p.id);
                        setErro('');
                      }}
                    >
                      {aberto === p.id ? 'Fechar' : 'Vincular a um exame'}
                    </Botao>
                    <Botao
                      variante="perigo"
                      tamanho="sm"
                      carregando={descartar.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            'Descartar esta imagem?\n\n'
                              + 'O arquivo é apagado e não há como recuperar. '
                              + 'A sala de RX para de enviá-la: ela não volta para esta tela '
                              + 'nas próximas leituras do agente.',
                          )
                        ) {
                          descartar.mutate(p.id);
                        }
                      }}
                    >
                      Descartar
                    </Botao>
                  </div>
                </div>

                {aberto === p.id && (
                  <div className="preso__sugestoes">
                    <p className="rotulo">Exames que combinam com esta imagem</p>
                    {sugestoes.isPending ? (
                      <p className="vazio">Procurando…</p>
                    ) : (sugestoes.data?.itens.length ?? 0) === 0 ? (
                      <div className="vazio">
                        <p className="vazio__titulo">Nenhum exame parecido</p>
                        <p>
                          Cadastre o exame na fila e volte aqui, ou descarte se for imagem de
                          teste.
                        </p>
                      </div>
                    ) : (
                      <ul className="sugestoes">
                        {sugestoes.data?.itens.map((s) => (
                          <li key={s.id}>
                            <button
                              className="sugestao"
                              onClick={() => vincular.mutate({ id: p.id, exameId: s.id })}
                              disabled={vincular.isPending}
                            >
                              <Chapa codigo={s.codigo} estagio={s.situacao} />
                              <span className="sugestao__nome">{s.pacienteNome}</span>
                              <span className="sugestao__exame">{s.procedimentoNome}</span>
                              <span className="mono sugestao__data">{dataBr(s.dataExame)}</span>
                              <Selo estagio={s.situacao} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Botao variante="silencioso" tamanho="sm" onClick={() => navegar('/exames/novo')}>
                      Cadastrar um exame novo para esta imagem
                    </Botao>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Layout>
  );
}
